import React, { useState, useEffect, memo, useMemo } from 'react';
import { useMEMTranslation } from '../../utils/text-utils';
import { useTrackInteraction } from '../../services/logging/hooks';
import { usePageContext } from '../../utils/page-context';
import { useAvailableParticipationPartitionCadences } from './hooks';
import { Option, Combobox } from '../../providers/harmony-scope/lazy-enablers';
import { EPartitionCadence } from '../../ducks/filters/types-filters';
import { useAppSelector } from '../../ducks/hooks';
import { useIsSinglePaylinePlan } from '../../utils/participation-incentive-type-hook';
import { useFlagValue } from '../../services/feature-flags/hooks';
import { Flags } from '../../services/feature-flags/constants-flags';
import { useIsBusinessImpersonating } from '../../utils/impersonation-utils';

interface IProps {
  selectedParticipation: string;
  selectedPartitionGroup: string;
  selectedCadence: EPartitionCadence;
  onChange: (partitionCadence: EPartitionCadence) => void;
}

const componentName = 'Participation Cadence Picker Filters Panel';
export const ComponentFilterPerformanceCadence = memo((props: IProps) => {
  // Props Accessors
  const {
    selectedParticipation: selectedParticipationProp,
    selectedPartitionGroup: selectedParticipationPartitionGroupProp,
    selectedCadence: selectedCadenceProp,
    onChange,
  } = props;

  // Hooks
  const { t } = useMEMTranslation();
  const pageName = usePageContext();
  const trackInteraction = useTrackInteraction();
  const participationDetails = useAppSelector((state) => state.participationDetails);
  const isSPL = useIsSinglePaylinePlan();
  const monthlySPLEnabled = useFlagValue(Flags.MONTHLY_SPL_ENABLED);
  const isBusinessImpersonating = useIsBusinessImpersonating();
  const availableCadences = useAvailableParticipationPartitionCadences(selectedParticipationProp, selectedParticipationPartitionGroupProp);
  const [selectedCadence, setSelectedCadence] = useState(selectedCadenceProp);

  // ensure monthly option exists
  const partitionOptionExists = useMemo(
    () => availableCadences.some((partitionGroup) => partitionGroup.partitionCadence === selectedCadence),
    [availableCadences, selectedCadence]
  );

  const isUbi = useMemo(() => {
    const selectedParticipation = participationDetails?.coreParticipations.find(
      (participation) =>
        participation.participationId === Number(selectedParticipationProp) &&
        participation.fiscalYearId === Number(selectedParticipationPartitionGroupProp)
    );
    if (selectedParticipation?.payMixDetails.incentiveType.toLocaleUpperCase() === 'UBI') {
      return true;
    }
    return false;
  }, [participationDetails?.coreParticipations, selectedParticipationPartitionGroupProp, selectedParticipationProp]);

  const shownCadences = useMemo(() => {
    const monthlyPartitionOptionExists = availableCadences.some((partitionGroup) => partitionGroup.partitionCadence === EPartitionCadence.Monthly);

    // Allow monthly for: non-UBI AND (non-SPL OR SPL with flag enabled OR business impersonation)
    const shouldShowMonthly = !isUbi && (!isSPL || monthlySPLEnabled || isBusinessImpersonating);

    return availableCadences.length > 0 && !monthlyPartitionOptionExists && shouldShowMonthly
      ? [...availableCadences, { partitionCadence: EPartitionCadence.Monthly, partitions: [] }]
      : [...availableCadences];
  }, [availableCadences, isUbi, isSPL, monthlySPLEnabled, isBusinessImpersonating]);

  // Effects
  useEffect(() => {
    setSelectedCadence(selectedCadenceProp);
  }, [selectedCadenceProp]);

  useEffect(() => {
    onChange(selectedCadence);
  }, [onChange, partitionOptionExists, selectedCadence]);

  useEffect(() => {
    // When the availableCadances change, if the selected cadence is not in the list, select the first one
    if (
      !shownCadences.find((cadence) => cadence.partitionCadence === selectedCadence) &&
      shownCadences.length > 0 &&
      shownCadences[0].partitionCadence
    ) {
      setSelectedCadence(shownCadences[0].partitionCadence);
    }
  }, [shownCadences, selectedCadence]);

  // Handlers
  const cadenceChanged = (event: any) => {
    if (!event.target.value) return;
    const newPartitionCadence = event.target.value;
    trackInteraction('Selected participation cadence', pageName, componentName, { selectedParticipationCadence: newPartitionCadence });
    setSelectedCadence(newPartitionCadence);
  };

  return shownCadences.length > 0 ? (
    <>
      <Combobox label={t('filtersPanelPartitionCadenceLabel')} value={selectedCadence} onHeChange={cadenceChanged}>
        {shownCadences.map((p) => (
          <Option key={`${p.partitionCadence}`} value={`${p.partitionCadence}`} title={`${p.partitionCadence}`}>
            {`${p.partitionCadence}`}
          </Option>
        ))}
      </Combobox>
      <br />
    </>
  ) : null;
});
